import * as Sentry from '@sentry/remix';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import type { AppleLoginAuth, FetcherData } from 'types';

import AppleIcon from '~/assets/icons/Apple';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import { useRegistrationMethod } from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';
import { getPageViewId } from '~/utils/navigationContext';

import type { Props } from '.';
import {
  socialButtonClassNames,
  SocialButtonLabel
} from './SocialButtonAppearance';

export default function AppleLoginButton({
  appearance = 'tile',
  onError,
  onSuccess,
  pagePosition,
  postAuthOptions
}: Props) {
  const user = useUser();
  const { t } = useTranslation('common');
  const fetcher = useFetcher<FetcherData>();
  const { isBot } = useRootData();
  const recordMethod = useRegistrationMethod();
  const [appleAuth, setAppleAuth] = useState<
    AppleLoginAuth['auth'] | undefined
  >();

  useEffect(() => {
    let loadAttempts = 0;

    const loadAppleAuth = () => {
      if (window.AppleID?.auth) {
        setAppleAuth(window.AppleID.auth);
        clearInterval(interval);
      } else if (loadAttempts >= 5) {
        clearInterval(interval);
      }

      loadAttempts += 1;
    };

    const interval = setInterval(loadAppleAuth, 200);

    return () => clearInterval(interval);
  }, []);

  const errors = fetcher.data?.errors;
  const submitting = fetcher.state === 'submitting';

  async function onLoginClick() {
    new SignUpMethodEvent({
      buttonName: 'SIGN_IN_APPLE',
      isBot,
      pagePosition: pagePosition || ''
    }).send();
    recordMethod('apple');

    let appleAuthToken = '';

    try {
      onError(null);

      if (!appleAuth) throw new Error('Apple auth not available');

      const { authorization } = await appleAuth.signIn();

      appleAuthToken = authorization.id_token;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { message: 'Apple auth failed' }
      });

      return onError(t('login.socialLoginError', { name: 'Apple' }));
    }

    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        productId: postAuthOptions?.productId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: appleAuthToken,
        viewId: getPageViewId() ?? ''
      },
      { action: '/bolt/login/apple', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_APPLE',
      pagePosition
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Apple);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess?.();
  }, [onSuccess, user.id]);

  useEffect(() => {
    if (errors?.message) onError(errors.message);
  }, [errors, onError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  const isTile = appearance === 'tile';

  return (
    <button
      className={socialButtonClassNames[appearance]}
      onClick={onLoginClick}
      type="button"
    >
      <AppleIcon
        colour={isTile ? 'black' : 'white'}
        height={isTile ? '24' : '16'}
        width={isTile ? '24' : '16'}
      />
      <SocialButtonLabel
        appearance={appearance}
        isSubmitting={submitting}
        name="Apple"
      />
    </button>
  );
}
